import { useParams, Link } from 'react-router-dom';
import { featuredArticles, latestArticles } from '@/mocks/gossip';
import { getArticleContent } from '@/mocks/articleContent';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const allArticles = [...featuredArticles, ...latestArticles];
  const article = allArticles.find((a) => a.slug === slug);
  const content = slug ? getArticleContent(slug) : null;

  const seoSchema = article && content ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.imageUrl,
    "datePublished": article.date,
    "author": {
      "@type": "Person",
      "name": content.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "吃瓜网",
      "url": "http://lrayneca.cn/"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `http://lrayneca.cn/article/${article.slug}`
    }
  } : null;

  const breadcrumbSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://lrayneca.cn/" },
      { "@type": "ListItem", "position": 2, "name": article.category, "item": `http://lrayneca.cn/category/${article.categorySlug}` },
      { "@type": "ListItem", "position": 3, "name": article.title }
    ]
  } : null;

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-emotion-sad-line text-3xl text-secondary-500"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">这颗瓜可能已经被吃完了，去看看别的吧</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页继续吃瓜
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = allArticles
    .filter(
      (a) =>
        a.slug !== slug &&
        (a.category === article.category || a.tags.some((t) => article.tags.includes(t)))
    )
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      {seoSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(seoSchema) }} />
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      )}
      <Header />

      <main className="pt-16">
        <article>
          <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 -mt-20 relative z-10">
            <div className="bg-background-50 rounded-2xl p-6 md:p-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full hover:bg-accent-200 transition-colors whitespace-nowrap"
                >
                  {article.category}
                </Link>
                {article.hot && (
                  <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-full flex items-center gap-1">
                    <i className="ri-fire-line text-[10px]"></i>
                    爆款
                  </span>
                )}
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000
                    ? `${(article.views / 10000).toFixed(1)}万 阅读`
                    : `${article.views} 阅读`}
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 pb-6 border-b border-background-200 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                    <i className="ri-user-line text-secondary-600 text-sm"></i>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-foreground-800">{content.author}</span>
                    <span className="text-xs text-foreground-500 block">{content.source}</span>
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-foreground-500">分享到：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-foreground-900 hover:text-background-50 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-foreground-900 hover:text-background-50 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-foreground-900 hover:text-background-50 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link-m text-sm"></i>
                  </button>
                </div>
              </div>

              <div
                className="prose prose-foreground max-w-none
                  prose-p:text-foreground-800 prose-p:leading-relaxed prose-p:text-[15px] prose-p:mb-4
                  prose-h3:text-foreground-900 prose-h3:text-xl prose-h3:font-bold prose-h3:mt-8 prose-h3:mb-4 prose-h3:font-heading
                  prose-strong:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: content.body }}
              />

              <div className="mt-8 pt-6 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-20 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">
                  相关猛料推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group rounded-2xl overflow-hidden bg-background-50 hover:bg-background-50 transition-all cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={ra.imageUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[11px] font-medium text-accent-700 bg-accent-100 px-2 py-0.5 rounded-full">
                            {ra.category}
                          </span>
                          <span className="text-xs text-foreground-500">{ra.date}</span>
                        </div>
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2 leading-snug">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
}
